import { useEffect, useState } from "react";
import { getRiderWallet, getRiderStats, createRiderVirtualAccount, withdrawRiderWallet } from "../../api/client";
import RiderNav from "../../components/RiderNav";
import PayoutAccountForm from "../../components/PayoutAccountForm";
import RiderProfileStatus from "../../components/RiderProfileStatus";
import LoadingIndicator from "../../components/LoadingIndicator";

export default function RiderWallet() {
  const [wallet, setWallet] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [vaBusy, setVaBusy] = useState(false);
  const [vaError, setVaError] = useState("");
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawMessage, setWithdrawMessage] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [wRes, sRes] = await Promise.all([getRiderWallet(), getRiderStats()]);
      setWallet(wRes.data);
      setStats(sRes.data);
    } catch {
      setError("Could not load rider wallet information.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const netBalance = wallet?.netBalance ?? 0;
  const isNegative = netBalance < 0;

  const handleCreateVirtualAccount = async () => {
    setVaBusy(true);
    setVaError("");
    try {
      const { data } = await createRiderVirtualAccount("paystack");
      setStats((s) => ({ ...s, virtualAccount: data.virtualAccount }));
    } catch (err) {
      setVaError(err.response?.data?.error || "Could not create your virtual account. Try again shortly.");
    } finally {
      setVaBusy(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawBusy(true);
    setWithdrawMessage("");
    setError("");
    try {
      const { data } = await withdrawRiderWallet();
      setWithdrawMessage(
        data.transferResult?.status === "success" || data.transferResult?.isMocked
          ? `✓ ₦${data.netPaidOut.toLocaleString()} sent to your bank account.`
          : `Withdrawal recorded (₦${data.netPaidOut.toLocaleString()}) — bank transfer is processing.`
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Withdrawal failed.");
    } finally {
      setWithdrawBusy(false);
    }
  };

  return (
    <div className="rider-app">
      <RiderNav />
      <div className="page wide page-with-nav">

      <RiderProfileStatus />

      <div className="brand-header">
        <span className="brand-logo">💳</span>
        <span className="brand-name">Dispatcher Wallet &amp; Earnings</span>
        <p className="brand-tagline">Track your earnings, cash commission debits, and balance ledger</p>
      </div>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <LoadingIndicator label="Loading wallet..." />
      ) : (
        <>
          {/* NET BALANCE BANNER */}
          <div className="delivery-card" style={{ textAlign: "center", marginBottom: "20px" }}>
            <p className="hint" style={{ marginBottom: "4px" }}>Net Wallet Balance</p>
            <div style={{ fontSize: "38px", fontWeight: "800", color: isNegative ? "var(--danger)" : "var(--success)" }}>
              {isNegative ? `-₦${Math.abs(netBalance).toLocaleString()}` : `₦${netBalance.toLocaleString()}`}
            </div>
            {isNegative ? (
              <div style={{ marginTop: "10px", padding: "10px 16px", background: "rgba(239, 68, 68, 0.12)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "var(--radius-md)", color: "var(--danger-hover)", fontSize: "13px" }}>
                ⚠️ <strong>Negative Balance Warning:</strong> You owe <strong>₦{Math.abs(netBalance).toLocaleString()}</strong> in platform commission for Cash-on-Delivery (COD) orders you fulfilled. Commission is automatically deducted from future digital earnings.
              </div>
            ) : (
              <>
                <p className="hint" style={{ marginTop: "6px" }}>
                  Positive balance ready for payout.
                </p>
                {withdrawMessage && <p className="hint" style={{ color: "var(--success)", marginTop: "6px" }}>{withdrawMessage}</p>}
                <button
                  className="success"
                  style={{ width: "auto", marginTop: "12px", padding: "10px 24px" }}
                  disabled={withdrawBusy || netBalance <= 0}
                  onClick={handleWithdraw}
                >
                  {withdrawBusy ? "Withdrawing..." : `Withdraw ₦${netBalance.toLocaleString()}`}
                </button>
              </>
            )}
          </div>

          {/* VIRTUAL ACCOUNT — "money in": anyone can bank-transfer straight
              into this account number and it lands in the wallet automatically. */}
          <div className="delivery-card" style={{ marginBottom: "24px" }}>
            <h3>💳 Your Virtual Account</h3>
            {stats?.virtualAccount?.status === "active" ? (
              <>
                <p className="hint" style={{ marginBottom: "12px" }}>
                  Share this account to receive payments directly into your wallet — any bank transfer here credits you automatically.
                </p>
                <div className="info-row">
                  <span className="label">Account Number</span>
                  <span className="value" style={{ fontSize: "18px", letterSpacing: "1px" }}>{stats.virtualAccount.accountNumber}</span>
                </div>
                <div className="info-row">
                  <span className="label">Bank</span>
                  <span className="value">{stats.virtualAccount.bankName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Account Name</span>
                  <span className="value">{stats.virtualAccount.accountName}</span>
                </div>
              </>
            ) : (
              <>
                <p className="hint" style={{ marginBottom: "12px" }}>
                  Get your own dedicated bank account number — anyone can transfer money straight into it and it credits your wallet instantly.
                </p>
                {vaError && <p className="error">{vaError}</p>}
                <button disabled={vaBusy} onClick={handleCreateVirtualAccount}>
                  {vaBusy ? "Creating..." : "Create My Virtual Account"}
                </button>
              </>
            )}
          </div>

          {/* PAYOUT ACCOUNT — required before the Withdraw button above will work */}
          <div style={{ marginBottom: "24px" }}>
            <PayoutAccountForm
              payoutAccount={stats?.payoutAccount}
              onUpdated={(payoutAccount) => setStats((s) => ({ ...s, payoutAccount }))}
            />
          </div>

          {/* STATS METRICS GRID */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "24px" }}>
            <div className="delivery-card" style={{ padding: "16px" }}>
              <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>Digital Earnings</span>
              <strong style={{ fontSize: "20px", color: "var(--text-accent)" }}>₦{(wallet?.earningsBalance || 0).toLocaleString()}</strong>
              <p className="hint" style={{ fontSize: "11px", marginTop: "2px" }}>Credited from card/wallet orders</p>
            </div>

            <div className="delivery-card" style={{ padding: "16px" }}>
              <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>COD Commission Debits</span>
              <strong style={{ fontSize: "20px", color: "var(--danger-hover)" }}>₦{(wallet?.cashOwedBalance || 0).toLocaleString()}</strong>
              <p className="hint" style={{ fontSize: "11px", marginTop: "2px" }}>Deducted from cash collected</p>
            </div>

            <div className="delivery-card" style={{ padding: "16px" }}>
              <span style={{ fontSize: "12px", color: "var(--text-muted)", display: "block" }}>Completed Deliveries</span>
              <strong style={{ fontSize: "20px", color: "var(--text-primary)" }}>{stats?.totalCompleted || 0}</strong>
              <p className="hint" style={{ fontSize: "11px", marginTop: "2px" }}>⭐ {stats?.rating?.toFixed(1) || "5.0"} Rating</p>
            </div>
          </div>

          {/* TRANSACTION LEDGER */}
          <h3>Wallet Transaction Log</h3>
          {!wallet?.transactions || wallet.transactions.length === 0 ? (
            <div className="empty-state">
              <span className="empty-state-icon">📋</span>
              <p className="empty-state-text">No wallet transactions recorded yet.</p>
            </div>
          ) : (
            wallet.transactions.map((tx, idx) => {
              // "earning" and "wallet_funding" both add to the balance; only
              // "commission-debit" and "payout" take money out.
              const isCredit = tx.type === "earning" || tx.type === "wallet_funding";
              const isDebit = tx.type === "commission-debit";
              const label = tx.type === "earning" ? "▲ Digital Order Earning"
                : tx.type === "wallet_funding" ? "💳 Virtual Account Transfer"
                : isDebit ? "▼ COD Commission Deduction"
                : "🤝 Payout";
              return (
                <div key={tx._id || idx} className="history-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <strong style={{ color: isCredit ? "var(--success)" : "var(--danger-hover)" }}>
                      {label}
                    </strong>
                    <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>
                      {tx.order ? `Order #${tx.order._id?.slice(-6).toUpperCase()} (${tx.order.paymentMethod?.toUpperCase()})` : "General Transaction"}
                      {tx.createdAt && ` · ${new Date(tx.createdAt).toLocaleString()}`}
                    </p>
                  </div>
                  <strong style={{ fontSize: "16px", color: isCredit ? "var(--success)" : "var(--danger-hover)" }}>
                    {isCredit ? `+₦${tx.amount?.toLocaleString()}` : `-₦${tx.amount?.toLocaleString()}`}
                  </strong>
                </div>
              );
            })
          )}
        </>
      )}
      </div>
    </div>
  );
}
